/**
 * Logger Service
 * 
 * A lightweight logging service that provides different log levels
 * and only logs in development mode to avoid console pollution in production.
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4
}

interface LogConfig {
  level: LogLevel;
  enableInProduction: boolean;
  prefix: string;
}

class LoggerService {
  private config: LogConfig = {
    level: import.meta.env.DEV ? LogLevel.DEBUG : LogLevel.ERROR,
    enableInProduction: false,
    prefix: '[DiskRay]'
  };

  private shouldLog(level: LogLevel): boolean {
    if (!this.config.enableInProduction && import.meta.env.PROD) {
      return false;
    }
    return level >= this.config.level;
  }

  private formatMessage(level: string, module: string, message: string): string {
    const timestamp = new Date().toISOString();
    return `${this.config.prefix} [${timestamp}] [${level}] [${module}] ${message}`;
  }

  debug(module: string, message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(this.formatMessage('DEBUG', module, message), ...args);
    }
  }

  info(module: string, message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.info(this.formatMessage('INFO', module, message), ...args);
    }
  }

  warn(module: string, message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(this.formatMessage('WARN', module, message), ...args);
    }
  }

  error(module: string, message: string, error?: any): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(this.formatMessage('ERROR', module, message), error);
      
      // In production, you might want to send errors to a monitoring service
      if (import.meta.env.PROD && error) {
        // Example: Send to Sentry or similar service
        // Sentry.captureException(error);
      }
    }
  }

  // Configuration methods
  setLevel(level: LogLevel): void {
    this.config.level = level;
  }

  setPrefix(prefix: string): void {
    this.config.prefix = prefix;
  }

  enableInProduction(): void {
    this.config.enableInProduction = true;
  }

  disableInProduction(): void {
    this.config.enableInProduction = false;
  }

  // Group logging for better organization
  group(module: string, label: string): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.group(`${this.config.prefix} [${module}] ${label}`);
    }
  }

  groupEnd(): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.groupEnd();
    }
  }

  // Table logging for structured data
  table(module: string, data: any[], columns?: string[]): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(`${this.config.prefix} [${module}] Table:`);
      console.table(data, columns);
    }
  }

  // Timing utilities
  private timers: Map<string, number> = new Map();

  time(module: string, label: string): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      const key = `${module}:${label}`;
      this.timers.set(key, performance.now());
      this.debug(module, `Timer started: ${label}`);
    }
  }

  timeEnd(module: string, label: string): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      const key = `${module}:${label}`;
      const start = this.timers.get(key);
      if (start !== undefined) {
        const duration = performance.now() - start;
        this.debug(module, `Timer ended: ${label} - ${duration.toFixed(2)}ms`);
        this.timers.delete(key);
      }
    }
  }
}

// Export singleton instance
export const logger = new LoggerService();

// Export convenience functions for common modules
export const authLogger = {
  debug: (message: string, ...args: any[]) => logger.debug('Auth', message, ...args),
  info: (message: string, ...args: any[]) => logger.info('Auth', message, ...args),
  warn: (message: string, ...args: any[]) => logger.warn('Auth', message, ...args),
  error: (message: string, error?: any) => logger.error('Auth', message, error)
};

export const apiLogger = {
  debug: (message: string, ...args: any[]) => logger.debug('API', message, ...args),
  info: (message: string, ...args: any[]) => logger.info('API', message, ...args),
  warn: (message: string, ...args: any[]) => logger.warn('API', message, ...args),
  error: (message: string, error?: any) => logger.error('API', message, error)
};

export const wsLogger = {
  debug: (message: string, ...args: any[]) => logger.debug('WebSocket', message, ...args),
  info: (message: string, ...args: any[]) => logger.info('WebSocket', message, ...args),
  warn: (message: string, ...args: any[]) => logger.warn('WebSocket', message, ...args),
  error: (message: string, error?: any) => logger.error('WebSocket', message, error)
};

export const paymentLogger = {
  debug: (message: string, ...args: any[]) => logger.debug('Payment', message, ...args),
  info: (message: string, ...args: any[]) => logger.info('Payment', message, ...args),
  warn: (message: string, ...args: any[]) => logger.warn('Payment', message, ...args),
  error: (message: string, error?: any) => logger.error('Payment', message, error)
};